/**
 * Route-aware defaults for the three conversation shell modes.
 *
 * Modes:
 *   - `peek`     — slim pill at bottom; tapping it expands to the last mode
 *                  the user chose for the route (or the route default).
 *   - `detailed` — chat input bar + (optional) last Anaka response card
 *                  visible above; dashboard widgets still rendered below.
 *   - `focused`  — full-page thread, widgets hidden, transparent thread
 *                  surface so the page background shows through.
 *
 * Any saved `chatLayoutPref` value not in the three-mode enum falls back to
 * the route default. There is no migration shim for the legacy two-mode
 * preference (`'focus'`); a user with an old value simply gets the new
 * route default on first read.
 */

import type { ChatLayoutMode } from '@shared/v2-schemas';

const V2_PROJECT_TABS = [
  'overview',
  'research',
  'participants',
  'interviews',
  'recommendations',
  'meetings',
  'insights',
  'chat',
] as const;

const V2_PROJECT_TAB_RE = new RegExp(
  `^/projects/[^/]+/(?:${V2_PROJECT_TABS.join('|')})$`,
);

/** Phase 4: Bet Detail deep-link surface — chat shell still mounts. */
const V2_PROJECT_BET_RE = /^\/projects\/[^/]+\/bet\/[^/]+$/;

const V2_CONVERSATION_RE = /^\/conversation\/[^/]+$/;

const CHAT_LAYOUT_MODES: readonly ChatLayoutMode[] = ['peek', 'detailed', 'focused'];

/** True on `/projects/:shortId/overview` (project overview tab). */
export function isProjectOverviewPath(pathname: string): boolean {
  const p = pathname === '' ? '/' : pathname;
  return /^\/projects\/[^/]+\/overview$/.test(p);
}

/** True when pathname is a v2 conversation UI surface (ConversationShell may mount). */
export function isV2ConversationSurface(pathname: string): boolean {
  const p = pathname === '' ? '/' : pathname;
  if (p === '/') return true;
  if (p === '/chat') return true;
  if (V2_PROJECT_TAB_RE.test(p)) return true;
  if (V2_PROJECT_BET_RE.test(p)) return true;
  if (V2_CONVERSATION_RE.test(p)) return true;
  return false;
}

/**
 * Route default for the chat shell mode.
 *
 * Project tab routes (`/projects/:id/<tab>`), bet detail routes
 * (`/projects/:id/bet/:betId`), and conversation routes
 * (`/conversation/:id`) default to `'detailed'` so the chat input
 * bar is immediately visible without requiring the user to tap the
 * peek pill.
 *
 * Home (`/`) defaults to `'focused'`. In the welcome state (no turns yet)
 * `<ConversationShell>` is suppressed and `<HomeLobby>` owns the input surface;
 * once the home conversation starts, the shell renders a full focused thread
 * (transcript + input bar) rather than collapsing to a peek pill — the lobby
 * conversation is the user's primary surface, so it should stay focused like
 * the other conversation screens. Unknown routes fall back to `'peek'`.
 *
 * `conversationHasUserMessages` is accepted for API compatibility but
 * no longer changes the return value.
 */
export function getDefaultModeForRoute(
  pathname: string,
  _conversationHasUserMessages: boolean = false,
): ChatLayoutMode {
  const p = pathname === '' ? '/' : pathname;
  if (p === '/') return 'focused';
  // `/chat` is the dedicated full-page home conversation — always focused (no
  // dashboard behind it to warrant peek/detailed). The shell hard-forces this
  // regardless of saved pref; this default keeps non-shell callers consistent.
  if (p === '/chat') return 'focused';
  if (V2_PROJECT_TAB_RE.test(p)) return 'detailed';
  if (V2_PROJECT_BET_RE.test(p)) return 'detailed';
  if (V2_CONVERSATION_RE.test(p)) return 'detailed';
  return 'peek';
}

/**
 * Returns true when the URL search string contains `?from=chat`, which
 * signals that the chat composer initiated this navigation. Callers
 * (e.g. ConversationShell) use this to start in `focused` mode once,
 * then strip the param via `replace` so refresh doesn't re-trigger.
 */
export function resolveChatNavIntent(search: string): boolean {
  return new URLSearchParams(search).get('from') === 'chat';
}

/**
 * Narrow an unknown saved value to a valid `ChatLayoutMode`, or `null` if
 * the value doesn't fit the three-mode enum. Used to discard legacy values
 * (e.g. `'focus'`) without a migration shim.
 */
export function coerceChatLayoutMode(value: unknown): ChatLayoutMode | null {
  return typeof value === 'string' && (CHAT_LAYOUT_MODES as readonly string[]).includes(value)
    ? (value as ChatLayoutMode)
    : null;
}

/**
 * Resolve the effective mode given a saved preference and the route. If the
 * saved value is missing or not in the three-mode enum, the route default
 * is returned.
 */
export function resolveChatLayoutMode(
  pathname: string,
  conversationHasUserMessages: boolean,
  savedPref: unknown,
): ChatLayoutMode {
  return coerceChatLayoutMode(savedPref) ?? getDefaultModeForRoute(pathname, conversationHasUserMessages);
}

export function getRouteBucket(pathname: string): 'overview' | 'other' {
  const p = pathname === '' ? '/' : pathname;
  return /^\/projects\/[^/]+\/overview$/.test(p) ? 'overview' : 'other';
}
